import { useAuthContext } from 'contexts';
import type { Annotation, Communication } from 'fhir/r4';
import { FHIRProxyClient, ProvenanceBuilder } from 'fhir-sdoh';
import moment from 'moment';
import type React from 'react';
import { useState } from 'react';
import type { CommunicationData } from 'types';
import { ErrorResponse } from 'utils';

import { PROXY_URL } from './fixtures';

type UseCommunicationNoteProps = {
  communicationPayload: Communication;
  isUnknownCaller: boolean;
  noteList: Annotation[];
  orgId?: string | undefined;
  patientId: string | undefined;
  refresh: () => Promise<CommunicationData | void>;
  subject: string | undefined;
};

const useCommunicationNote = ({
  communicationPayload,
  isUnknownCaller,
  noteList,
  orgId,
  patientId,
  refresh,
  subject,
}: UseCommunicationNoteProps) => {
  const { practitionerName, practitionerId, token } = useAuthContext();

  const [note, setNote] = useState({ noteText: '', noteTag: [] });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedNote, setSelectedNote] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleSelectedNote = (note: string | null) => {
    setSelectedNote(note);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const fhirProxyClient = new FHIRProxyClient(token, PROXY_URL);
    const noteAlerts = [];

    const noteObject = {
      authorString: practitionerName,
      text: note.noteText,
      time: moment().toISOString(),
    };

    setIsSubmitting(true);
    setValidationErrors([]);
    communicationPayload.note = [noteObject, ...noteList];

    try {
      // Try to update communication
      await fhirProxyClient.send('put', `Communication/${communicationPayload.id}`, communicationPayload);
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      setValidationErrors((state) => [...state, `Unable to add communication note: ${errorResponse}`]);
    }

    try {
      if (!isUnknownCaller) {
        const provenance = ProvenanceBuilder.build(
          `Communication/${communicationPayload.id}`,
          { reference: `Practitioner/${practitionerId}`, display: practitionerName },
          patientId,
          'UPDATE',
          `A note (${note.noteText}) has been added to ${subject} Communication`,
          { code: orgId?.toUpperCase(), display: orgId?.toLowerCase() }
        );

        const fhirBundle = ProvenanceBuilder.buildProvenanceBundle(noteAlerts, provenance);

        await fhirProxyClient.send('post', '', fhirBundle);
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the note list stale
      refresh();
      setNote({ noteText: '', noteTag: [] });
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      setValidationErrors((state) => [...state, `Unable to create provenance: ${errorResponse}`]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeNote = async (note: string) => {
    const noteId = parseInt(note);
    setSelectedNote(null);
    setValidationErrors([]);

    const communicationPayloadTemp = communicationPayload;
    const removedNote = communicationPayloadTemp.note!.splice(noteId, 1); // remove selected note

    const fhirProxyClient = new FHIRProxyClient(token, PROXY_URL);

    setIsDeleting(true);

    try {
      // Try to update communication
      await fhirProxyClient.send('put', `Communication/${communicationPayloadTemp.id}`, communicationPayloadTemp);
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      setValidationErrors((state) => [...state, `Unable to remove communication note: ${errorResponse}`]);
    }

    try {
      if (!isUnknownCaller) {
        const provenance = ProvenanceBuilder.build(
          `Communication/${communicationPayloadTemp.id}`,
          { reference: `Practitioner/${practitionerId}`, display: practitionerName },
          patientId,
          'UPDATE',
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. `orgId` is optional — this file
         * derives `isUnknownCaller = !orgId` from exactly that — yet
         * `.toUpperCase()`/`.toLowerCase()` are called on it here. The block is
         * guarded by `if (!isUnknownCaller)`, so the two agree today; the guard and
         * the dereference are far apart and nothing ties them together, which is
         * what makes it fragile rather than wrong.
         *
         * `Communication.note` is `0..*`, so `note.splice(...)` above throws on a
         * communication that has never had a note, and `category[0].coding[0].code`
         * in the message below dereferences two `0..*` levels unguarded.
         */
          `A note (${removedNote[0]!.text}) has been remove from ${communicationPayloadTemp?.category?.[0]!.coding?.[0]!.code} communication`,
          { code: orgId!.toUpperCase(), display: orgId!.toLowerCase() }
        );

        const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([], provenance);

        await fhirProxyClient.send('post', '', fhirBundle);
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the note list stale
      refresh();
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      setValidationErrors((state) => [...state, `Unable to create provenance: ${errorResponse}`]);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    handleSelectedNote,
    handleSubmit,
    isDeleting,
    isSubmitting,
    note,
    removeNote,
    selectedNote,
    setNote,
    validationErrors,
  };
};

export default useCommunicationNote;
